import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import is from '@sindresorhus/is';
import {
    $getSelection,
    $isRangeSelection,
    $setSelection,
    type BaseSelection,
    COMMAND_PRIORITY_LOW,
    SELECTION_CHANGE_COMMAND,
    SKIP_DOM_SELECTION_TAG,
} from 'lexical';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { $wrapSelectionInCommentMark } from '~/ui/core/rich-editor/comment-mark-selection';

import { COMMENT_BUTTON_ATTRIBUTE } from './anchor/grammar';
import { useCommentableAnchor } from './anchor/use-commentable-anchor';
import { SelectionCommentButton } from './SelectionCommentButton';

type CommentSelectionPluginProps = {
    readonly itemId: string;
    readonly sectionId: string;
};

const isCommentButton = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement && !is.null(target.closest(`[${COMMENT_BUTTON_ATTRIBUTE}]`));

export const CommentSelectionPlugin: FunctionComponent<CommentSelectionPluginProps> = ({ itemId, sectionId }) => {
    const [editor] = useLexicalComposerContext();
    const { startComment } = useCommentableAnchor({ kind: 'description-section', itemId, sectionId });
    const [rect, setRect] = useState<DOMRect | null>(null);
    const selectionRef = useRef<BaseSelection | null>(null);

    useEffect(
        () =>
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    const selection = $getSelection();
                    if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                        selectionRef.current = selection.clone();
                    } else {
                        setRect(null);
                    }
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
        [editor],
    );

    useEffect(() => {
        const clear = (): void => {
            selectionRef.current = null;
            setRect(null);
        };

        const selectionRangeInEditor = (): Range | null => {
            const root = editor.getRootElement();
            const domSelection = window.getSelection();
            if (is.null(root) || is.null(domSelection) || domSelection.isCollapsed || domSelection.rangeCount === 0) {
                return null;
            }
            const range = domSelection.getRangeAt(0);
            if (
                range.toString().trim().length === 0 ||
                !root.contains(range.startContainer) ||
                !root.contains(range.endContainer)
            ) {
                return null;
            }
            return range;
        };

        const handlePointerDown = (event: MouseEvent): void => {
            if (isCommentButton(event.target)) {
                return;
            }
            clear();
        };

        const handlePointerUp = (event: MouseEvent): void => {
            if (isCommentButton(event.target)) {
                return;
            }
            const range = selectionRangeInEditor();
            if (is.null(range)) {
                return;
            }
            editor.getEditorState().read(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                    selectionRef.current = selection.clone();
                }
            });
            setRect(range.getBoundingClientRect());
        };

        const handleScroll = (): void => {
            clear();
        };

        document.addEventListener('mousedown', handlePointerDown, true);
        document.addEventListener('mouseup', handlePointerUp);
        document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        return () => {
            document.removeEventListener('mousedown', handlePointerDown, true);
            document.removeEventListener('mouseup', handlePointerUp);
            document.removeEventListener('scroll', handleScroll, { capture: true });
        };
    }, [editor]);

    const handleComment = (): void => {
        const savedSelection = selectionRef.current;
        if (is.null(savedSelection)) {
            return;
        }
        const anchorEl = editor.getRootElement();
        const id = crypto.randomUUID();
        let wrapped = false;
        editor.update(
            () => {
                $setSelection(savedSelection.clone());
                wrapped = $wrapSelectionInCommentMark(id);
            },
            { discrete: true, tag: ['external-sync', SKIP_DOM_SELECTION_TAG] },
        );
        if (!wrapped) {
            selectionRef.current = null;
            setRect(null);
            return;
        }
        startComment(anchorEl, { anchorRect: rect ?? new DOMRect(), commentId: id });
        selectionRef.current = null;
        setRect(null);
    };

    if (is.null(rect)) {
        return null;
    }

    return <SelectionCommentButton rect={rect} onComment={handleComment} />;
};
